<template>
	<div class="monetization-takeover-renderer">
		<video
			v-if="isAdbutlerTakeover"
			ref="adVideo"
			class="monetization-takeover-renderer__video"
			aria-hidden="true"
			muted
			playsinline
			preload="auto"
			@canplay="handleAdbutlerCanPlay"
			@ended="handleAdbutlerEnded"
			@error="handleAdbutlerMediaError"
			@pause="handleAdbutlerPause"
			@playing="handleAdbutlerPlaying"
			@timeupdate="handleAdbutlerTimeUpdate"
		/>
	</div>
</template>

<script>
import { VASTClient, VASTTracker } from '@dailymotion/vast-client';
import { mapGetters } from 'vuex';
import { EVENT, bus } from '@videotools/js/events';
import storage from '@shared/js/storage';
import { AdType } from '@shared/enums/AdType';
import { AdSource } from '@shared/enums/AdSource';
import { AdServerType } from '@shared/enums/AdServerType';
import { AdDisplayMode } from '@shared/enums/AdDisplayMode';
import { TakeoverPosition } from '@shared/enums/TakeoverPosition';
import { AdRotationMode } from '@shared/enums/AdRotationMode';
import {
	DEFAULT_DURATION_SECONDS,
	getIntervalDisplayTiming,
	getIntervalDisplaySlotIndex,
	isOnceDisplayMode,
} from '@components/player/monetization/displayTiming';
import { getEffectiveCreativeRotationMode } from '@components/player/monetization/creativeRotation';

const SEEK_POSITION_DELTA_SECONDS = 2.5;
const ADBUTLER_PREFETCH_SECONDS = 10;
const ADBUTLER_READINESS_GRACE_SECONDS = 5;
const ADBUTLER_MAX_PLAYBACK_SECONDS = 120;
const ADBUTLER_DEFAULT_INTERVAL_SECONDS = 300;
const vastClient = new VASTClient();

export default {
	name:  'MonetizationTakeoverRenderer',
	props: {
		playerIsAdMode: {
			type:    Boolean,
			default: false,
		},
		position: {
			type: Number,
		},
		playerWidth: {
			type:    Number,
			default: 0,
		},
		event: {
			type:    Object,
			default: null,
		},
		ads: {
			type:    Array,
			default: () => [],
		},
		isCouch: {
			type:    Boolean,
			default: false,
		},
		isManager: {
			type:    Boolean,
			default: false,
		},
		adbutlerLocale: {
			type:    String,
			default: '',
		},
		adbutlerLabels: {
			type:    Array,
			default: () => [],
		},
	},
	data() {
		return {
			timeout:                       null,
			activeImageLoadKey:            null,
			imageLoaded:                   false,
			imageLoadRequestId:            0,
			loadStartedAtPosition:         null,
			imageLoadedAtPosition:         null,
			onceDisplayRequested:          false,
			positionUpdated:               false,
			randomizedCreativeIds:         [],
			randomizedPass:                null,
			emittedStateKey:               null,
			adbutlerRequestId:             0,
			adbutlerPresentationSequence:  0,
			adbutlerObservedSlotIndex:     null,
			adbutlerWasInStartWindow:      false,
			adbutlerPreparedSlotIndex:     null,
			adbutlerPreparedKey:           null,
			adbutlerFailedKey:             null,
			adbutlerFailedSlotIndex:       null,
			adbutlerStartRequestedKey:     null,
			adbutlerVisible:               false,
			adbutlerReady:                 false,
			adbutlerTracker:               null,
			adbutlerCreative:              null,
			adbutlerReadinessGraceTimeout: null,
			adbutlerSafetyTimeout:         null,
		};
	},
	computed: {
		...mapGetters({
			isTaggingToolMode: 'app/isTaggingToolMode',
		}),
		ad() {
			return this.ads?.find(ad => ad.type === AdType.TAKEOVER_AD);
		},
		isAdbutlerTakeover() {
			return this.ad?.source === AdSource.ADSERVER && this.ad?.adserverType === AdServerType.ADBUTLER;
		},
		creatives() {
			const creatives = this.ad?.creatives?.length
				? this.ad.creatives
				: [];

			return [...creatives].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
		},
		currentCreative() {
			if (!this.creatives.length) {
				return null;
			}
			if (this.effectiveRotationMode === AdRotationMode.RANDOMIZED && this.randomizedCreativeIds.length) {
				const creativeId = this.randomizedCreativeIds[this.displaySlotIndex % this.randomizedCreativeIds.length];
				return this.creatives.find(creative => creative.id === creativeId) || this.creatives[0];
			}
			return this.creatives[this.displaySlotIndex % this.creatives.length];
		},
		effectiveRotationMode() {
			return getEffectiveCreativeRotationMode(this.displayMode, this.ad?.rotationMode, this.creatives.length);
		},
		displayMode() {
			return this.ad?.displayMode;
		},
		displayIntervalSeconds() {
			return this.ad?.displayIntervalSeconds;
		},
		displayDurationSeconds() {
			return this.ad?.displayDurationSeconds;
		},
		displaySlotIndex() {
			return getIntervalDisplaySlotIndex(this.displayMode, this.position, this.displayIntervalSeconds, this.displayDurationSeconds);
		},
		displayTiming() {
			return getIntervalDisplayTiming(this.displayMode, this.displayIntervalSeconds, this.displayDurationSeconds);
		},
		displaySlotPosition() {
			if (!this.displayTiming) {
				return 0;
			}

			return (Number(this.position) || 0) % this.displayTiming.intervalSeconds;
		},
		canDisplayAd() {
			return this.canRequestAdbutler && !(
				this.playerIsAdMode ||
				!this.positionUpdated
			);
		},
		canRequestAdbutler() {
			return !(
				this.isCouch ||
				(this.isAdbutlerTakeover && this.isManager) ||
				this.isTaggingToolMode ||
				!this.ad ||
				(!this.isAdbutlerTakeover && !this.currentCreative) ||
				(this.isAdbutlerTakeover && !window.arenaParams?.adButlerInstanceId)
			);
		},
		loadDelaySeconds() {
			if (!Number.isFinite(this.loadStartedAtPosition) || !Number.isFinite(this.imageLoadedAtPosition)) {
				return 0;
			}

			return Math.max(0, this.imageLoadedAtPosition - this.loadStartedAtPosition);
		},
		imageLoadKey() {
			if (this.isAdbutlerTakeover || !this.canDisplayAd || !this.currentCreative?.imageUrl) {
				return null;
			}

			if (this.displayTiming && this.displaySlotPosition < this.displayTiming.durationSeconds) {
				return [
					'interval',
					this.displaySlotIndex,
					this.currentCreative.id,
					this.currentCreative.imageUrl,
				].join('|');
			}

			if (isOnceDisplayMode(this.displayMode) && this.onceDisplayRequested) {
				return [
					'once',
					this.currentCreative.id,
					this.currentCreative.imageUrl,
				].join('|');
			}

			return null;
		},
		adVisible() {
			if (this.isAdbutlerTakeover || !this.canDisplayAd) {
				return false;
			}

			if (this.displayTiming) {
				return this.displaySlotPosition < this.displayTiming.durationSeconds + this.loadDelaySeconds;
			}

			if (isOnceDisplayMode(this.displayMode)) {
				return Boolean(this.timeout);
			}

			return false;
		},
		hasVast() {
			return this.ads.find(ad => ad.type === AdType.PLAYER_AD);
		},
		adbutlerIntervalSeconds() {
			if (this.displayMode === AdDisplayMode._10S_EACH_5M) {
				return ADBUTLER_DEFAULT_INTERVAL_SECONDS;
			}
			if (this.displayMode === AdDisplayMode._CUSTOM) {
				return Number(this.displayIntervalSeconds) || 0;
			}
			return 0;
		},
		state() {
			if (this.isAdbutlerTakeover) {
				if (!this.adbutlerVisible) {
					return null;
				}

				return {
					visible:            true,
					mediaType:          'video',
					link:               this.adbutlerCreative?.videoClickThroughURLTemplate?.url || null,
					onClick:            this.handleAdbutlerClick,
					playerScalePercent: this.ad.playerScalePercent || 80,
					takeoverPosition:   this.ad.takeoverPosition || TakeoverPosition.BOTTOM_LEFT,
					presentationKey:    this.adbutlerPreparedKey,
				};
			}

			if (!this.adVisible || !this.imageLoaded) {
				return null;
			}

			return {
				visible:             true,
				mediaType:           'image',
				imageUrl:            this.currentCreative.imageUrl,
				link:                this.appendAdAttributes(this.currentCreative.link),
				playerScalePercent:  this.ad.playerScalePercent || 80,
				takeoverPosition:    this.ad.takeoverPosition || TakeoverPosition.BOTTOM_LEFT,
				currentCreativeId:   this.currentCreative.id,
				currentCreativeLink: this.currentCreative.link,
			};
		},
	},
	watch: {
		position(value, previousValue) {
			this.positionUpdated = true;
			if (this.isAdbutlerTakeover) {
				if (this.onceDisplayRequested && this.canDisplayAd) {
					this.onceDisplayRequested = false;
					this.requestAdbutlerStart('once');
				}
				this.handleAdbutlerPosition(value);
				return;
			}
			if (
				this.activeImageLoadKey &&
				!this.imageLoaded &&
				Number.isFinite(value) &&
				Number.isFinite(previousValue) &&
				Math.abs(value - previousValue) > SEEK_POSITION_DELTA_SECONDS
			) {
				this.resetImageLoad();
			}
		},
		ad() {
			this.resetCreativeRotation();
			this.resetImageLoad();
			this.resetAdbutler(true);
			this.initializeAdbutler();
		},
		displaySlotIndex() {
			if (this.isAdbutlerTakeover) {
				return;
			}
			this.resetImageLoad();
			this.ensureRandomizedOrder();
		},
		canDisplayAd(value) {
			if (value && this.isAdbutlerTakeover) {
				if (this.onceDisplayRequested) {
					this.onceDisplayRequested = false;
					this.requestAdbutlerStart('once');
				} else {
					this.tryStartAdbutlerVideo(this.adbutlerStartRequestedKey);
				}
			} else if (this.isAdbutlerTakeover && this.adbutlerVisible) {
				this.finishAdbutlerPresentation({ trackClose: true });
			} else if (!value && !this.isAdbutlerTakeover) {
				this.resetImageLoad();
			}
		},
		canRequestAdbutler(value) {
			if (!value && this.isAdbutlerTakeover) {
				this.resetAdbutler(true);
			}
		},
		imageLoadKey(value) {
			if (value) {
				this.loadImage(value);
			}
		},
		state: {
			immediate: true,
			handler(value) {
				const stateKey = this.getStateKey(value);
				if (stateKey === this.emittedStateKey) {
					return;
				}

				this.emittedStateKey = stateKey;
				this.$emit('state-change', value);
			},
		},
	},
	mounted() {
		this.resetCreativeRotation();

		if (isOnceDisplayMode(this.displayMode)) {
			if (!this.hasVast) {
				this.startOnceDisplay();
			} else {
				bus.subscribe(EVENT.PLAYER.ON.MONETIZATION_AD_START, this.startOnceDisplay);
			}
		}

		this.initializeAdbutler();
	},
	destroyed() {
		this.$emit('state-change', null);
		clearTimeout(this.timeout);
		this.imageLoadRequestId += 1;
		this.resetAdbutler(true);
		if (this.hasVast && isOnceDisplayMode(this.displayMode)) {
			bus.unsubscribe(EVENT.PLAYER.ON.MONETIZATION_AD_START, this.startOnceDisplay);
		}
	},
	methods: {
		getStateKey(state) {
			if (!state) {
				return 'hidden';
			}

			return [
				state.mediaType,
				state.imageUrl,
				state.link,
				state.playerScalePercent,
				state.takeoverPosition,
				state.currentCreativeId,
				state.presentationKey,
			].join('|');
		},
		resetCreativeRotation() {
			this.randomizedCreativeIds = [];
			this.randomizedPass = null;
			this.ensureRandomizedOrder();
		},
		ensureRandomizedOrder() {
			if (this.effectiveRotationMode !== AdRotationMode.RANDOMIZED || !this.creatives.length) {
				return;
			}

			const pass = Math.floor(this.displaySlotIndex / this.creatives.length);
			if (this.randomizedPass === pass && this.randomizedCreativeIds.length === this.creatives.length) {
				return;
			}

			const previousLast = this.randomizedCreativeIds[this.randomizedCreativeIds.length - 1];
			const ids = this.creatives.map(creative => creative.id);
			for (let index = ids.length - 1; index > 0; index--) {
				const randomIndex = Math.floor(Math.random() * (index + 1));
				[ids[index], ids[randomIndex]] = [ids[randomIndex], ids[index]];
			}

			if (ids.length > 1 && ids[0] === previousLast) {
				[ids[0], ids[1]] = [ids[1], ids[0]];
			}

			this.randomizedCreativeIds = ids;
			this.randomizedPass = pass;
		},
		startOnceDisplay() {
			if (this.isAdbutlerTakeover) {
				this.onceDisplayRequested = true;
				if (this.canDisplayAd) {
					this.onceDisplayRequested = false;
					this.requestAdbutlerStart('once');
				}
				return;
			}

			this.startTenSecondOnceDisplay();
		},
		startTenSecondOnceDisplay() {
			clearTimeout(this.timeout);
			this.timeout = null;
			this.onceDisplayRequested = true;
			this.resetImageLoad();
		},
		loadImage(imageLoadKey) {
			if (this.activeImageLoadKey === imageLoadKey) {
				return;
			}

			this.resetImageLoad(false);
			this.activeImageLoadKey = imageLoadKey;
			this.loadStartedAtPosition = Number(this.position) || 0;

			const requestId = ++this.imageLoadRequestId;
			const image = new Image();
			let didFinishLoading = false;
			const finishLoading = () => {
				if (didFinishLoading || requestId !== this.imageLoadRequestId) {
					return;
				}

				didFinishLoading = true;
				this.imageLoaded = true;
				this.imageLoadedAtPosition = Number(this.position) || 0;

				if (isOnceDisplayMode(this.displayMode)) {
					this.startOnceDisplayTimer();
				}
			};

			image.onload = finishLoading;
			image.onerror = finishLoading;
			image.src = this.currentCreative.imageUrl;

			if (image.complete) {
				finishLoading();
			}
		},
		resetImageLoad(invalidateLoading = true) {
			if (invalidateLoading) {
				this.imageLoadRequestId += 1;
			}

			this.activeImageLoadKey = null;
			this.imageLoaded = false;
			this.loadStartedAtPosition = null;
			this.imageLoadedAtPosition = null;
		},
		startOnceDisplayTimer() {
			clearTimeout(this.timeout);
			this.onceDisplayRequested = false;
			this.timeout = setTimeout(() => {
				this.timeout = null;
				this.resetImageLoad();
			}, DEFAULT_DURATION_SECONDS * 1000);
		},
		initializeAdbutler() {
			if (!this.isAdbutlerTakeover || !this.canRequestAdbutler || !this.ad?.adbutlerZoneId || !this.ad?.adbutlerFormat) {
				return;
			}

			if (isOnceDisplayMode(this.displayMode)) {
				this.prepareAdbutlerPresentation('once', null);
			}
		},
		// Recurring ads are keyed by visits to the interval start window so seeks can intentionally create a new presentation.
		handleAdbutlerPosition(position) {
			if (!this.canDisplayAd || !this.adbutlerIntervalSeconds || !Number.isFinite(Number(position))) {
				return;
			}

			const numericPosition = Math.max(0, Number(position));
			const slotIndex = Math.floor(numericPosition / this.adbutlerIntervalSeconds);
			const slotPosition = numericPosition % this.adbutlerIntervalSeconds;
			const isInStartWindow = slotPosition < ADBUTLER_READINESS_GRACE_SECONDS;
			const enteredStartWindow = isInStartWindow && (slotIndex !== this.adbutlerObservedSlotIndex || !this.adbutlerWasInStartWindow);
			if (slotIndex !== this.adbutlerObservedSlotIndex || enteredStartWindow) {
				this.adbutlerObservedSlotIndex = slotIndex;
				const preparedKey = this.adbutlerPreparedSlotIndex === slotIndex && !this.adbutlerStartRequestedKey
					? this.adbutlerPreparedKey
					: null;
				const presentationKey = preparedKey || this.createAdbutlerPresentationKey(slotIndex);
				// A failed prefetch consumes the first visit, but seeking away and back creates a new presentation.
				if (enteredStartWindow && this.adbutlerFailedSlotIndex === slotIndex) {
					this.adbutlerFailedSlotIndex = null;
				} else if (enteredStartWindow) {
					this.requestAdbutlerStart(presentationKey, slotIndex);
				}
			}
			this.adbutlerWasInStartWindow = isInStartWindow;

			// A single media element cannot prefetch over an active ad, so replacement loads at the boundary in that case.
			if (slotPosition >= this.adbutlerIntervalSeconds - ADBUTLER_PREFETCH_SECONDS && !this.adbutlerVisible) {
				const nextSlotIndex = slotIndex + 1;
				if (this.adbutlerPreparedSlotIndex !== nextSlotIndex && this.adbutlerFailedSlotIndex !== nextSlotIndex) {
					this.prepareAdbutlerPresentation(this.createAdbutlerPresentationKey(nextSlotIndex), nextSlotIndex);
				}
			}
		},
		createAdbutlerPresentationKey(slotIndex) {
			this.adbutlerPresentationSequence += 1;
			return `interval-${slotIndex}-${this.adbutlerPresentationSequence}`;
		},
		requestAdbutlerStart(presentationKey, slotIndex = null) {
			if (!this.isAdbutlerTakeover || !this.canDisplayAd || this.adbutlerStartRequestedKey === presentationKey || this.adbutlerFailedKey === presentationKey) {
				return;
			}

			if (this.adbutlerVisible && this.adbutlerPreparedKey !== presentationKey) {
				this.finishAdbutlerPresentation({ trackClose: true });
			}

			if (this.adbutlerPreparedKey !== presentationKey) {
				this.prepareAdbutlerPresentation(presentationKey, slotIndex);
			}
			this.adbutlerStartRequestedKey = presentationKey;
			this.startAdbutlerReadinessGrace(presentationKey);
			this.tryStartAdbutlerVideo(presentationKey);
		},
		async prepareAdbutlerPresentation(presentationKey, slotIndex) {
			if (this.adbutlerPreparedKey === presentationKey || this.adbutlerFailedKey === presentationKey) {
				return;
			}

			this.clearAdbutlerMedia(false);
			this.adbutlerPreparedKey = presentationKey;
			this.adbutlerPreparedSlotIndex = slotIndex;
			const requestId = ++this.adbutlerRequestId;

			try {
				const vast = await vastClient.get(this.getAdbutlerVastUrl());
				if (requestId !== this.adbutlerRequestId) {
					return;
				}

				const selection = this.selectAdbutlerMedia(vast);
				if (!selection) {
					this.failAdbutlerPresentation(presentationKey, 'no playable Linear MP4');
					return;
				}

				this.adbutlerCreative = selection.creative;
				this.adbutlerTracker = new VASTTracker(vastClient, selection.ad, selection.creative);
				this.adbutlerTracker.on('clickthrough', this.openAdbutlerClickThrough);
				const video = this.$refs.adVideo;
				if (!video) {
					this.failAdbutlerPresentation(presentationKey, 'video element unavailable');
					return;
				}

				// The media element is reused across presentations; loading a new source also releases the previous buffer.
				video.src = selection.mediaFile.fileURL;
				video.muted = true;
				video.load();
			} catch (error) {
				if (requestId === this.adbutlerRequestId) {
					this.failAdbutlerPresentation(presentationKey, 'VAST request failed', error);
				}
			}
		},
		getAdbutlerVastUrl() {
			const url = new URL('https://servedbyadbutler.com/vast.spark');
			url.searchParams.set('setID', this.ad.adbutlerZoneId);
			url.searchParams.set('ID', window.arenaParams.adButlerInstanceId);
			url.searchParams.set('_abdk[language]', this.adbutlerLocale);
			url.searchParams.set('_abdk[format]', this.ad.adbutlerFormat);
			if (this.ad.adbutlerSendLabels && this.adbutlerLabels.length) {
				url.searchParams.set('keywords', this.adbutlerLabels.join(','));
			}
			return url.toString();
		},
		selectAdbutlerMedia(vast) {
			const video = this.$refs.adVideo;
			const targetWidth = (this.playerWidth || video?.clientWidth || 1920) * (window.devicePixelRatio || 1);
			const selections = [];
			for (const ad of vast?.ads || []) {
				for (const creative of ad.creatives || []) {
					if (creative.type !== 'linear') {
						continue;
					}
					for (const mediaFile of creative.mediaFiles || []) {
						if (!mediaFile.mimeType?.startsWith('video/mp4') || !mediaFile.fileURL || !video?.canPlayType(mediaFile.mimeType)) {
							continue;
						}
						const width = Number(mediaFile.width) || 0;
						// Prefer the closest file, with a penalty for choices the browser would need to upscale.
						const score = width >= targetWidth ? width - targetWidth : (targetWidth - width) * 2;
						selections.push({ ad, creative, mediaFile, score });
					}
				}
			}

			return selections.sort((first, second) => first.score - second.score)[0] || null;
		},
		startAdbutlerReadinessGrace(presentationKey) {
			clearTimeout(this.adbutlerReadinessGraceTimeout);
			this.adbutlerReadinessGraceTimeout = setTimeout(() => {
				if (!this.adbutlerVisible && this.adbutlerStartRequestedKey === presentationKey) {
					this.failAdbutlerPresentation(presentationKey, 'not ready within 5 seconds');
				}
			}, ADBUTLER_READINESS_GRACE_SECONDS * 1000);
		},
		handleAdbutlerCanPlay() {
			if (this.adbutlerReady) {
				return;
			}
			this.adbutlerReady = true;
			this.adbutlerTracker?.load();
			this.tryStartAdbutlerVideo(this.adbutlerStartRequestedKey);
		},
		tryStartAdbutlerVideo(presentationKey) {
			if (!this.canDisplayAd || !presentationKey || presentationKey !== this.adbutlerPreparedKey || !this.adbutlerReady || this.adbutlerVisible) {
				return;
			}

			const playPromise = this.$refs.adVideo?.play();
			if (playPromise?.catch) {
				playPromise.catch(error => this.failAdbutlerPresentation(presentationKey, 'media playback failed', error));
			}
		},
		handleAdbutlerPlaying() {
			if (!this.adbutlerStartRequestedKey) {
				return;
			}
			if (this.adbutlerVisible) {
				this.adbutlerTracker?.setPaused(false);
				return;
			}

			clearTimeout(this.adbutlerReadinessGraceTimeout);
			this.adbutlerReadinessGraceTimeout = null;
			this.adbutlerVisible = true;
			this.adbutlerTracker?.trackImpression();
			this.adbutlerTracker?.setMuted(true);
			this.adbutlerTracker?.setProgress(this.$refs.adVideo?.currentTime || 0);
			clearTimeout(this.adbutlerSafetyTimeout);
			this.adbutlerSafetyTimeout = setTimeout(() => {
				this.finishAdbutlerPresentation({ trackClose: true });
			}, ADBUTLER_MAX_PLAYBACK_SECONDS * 1000);
		},
		handleAdbutlerTimeUpdate() {
			if (this.adbutlerVisible) {
				this.adbutlerTracker?.setProgress(this.$refs.adVideo?.currentTime || 0);
			}
		},
		handleAdbutlerPause() {
			// Browsers emit pause as playback reaches its natural end; completion must not count as a viewer pause.
			if (this.adbutlerVisible && !this.$refs.adVideo?.ended) {
				this.adbutlerTracker?.setPaused(true);
			}
		},
		handleAdbutlerEnded() {
			if (!this.adbutlerVisible) {
				return;
			}
			this.adbutlerTracker?.setProgress(this.$refs.adVideo?.duration || this.$refs.adVideo?.currentTime || 0);
			this.adbutlerTracker?.complete();
			this.finishAdbutlerPresentation();
		},
		handleAdbutlerMediaError() {
			if (this.adbutlerPreparedKey) {
				this.adbutlerTracker?.error({ ERRORCODE: '405' });
				this.failAdbutlerPresentation(this.adbutlerPreparedKey, 'media error');
			}
		},
		handleAdbutlerClick() {
			this.adbutlerTracker?.click();
		},
		openAdbutlerClickThrough(url) {
			if (url) {
				window.open(url, '_blank', 'noopener');
			}
		},
		failAdbutlerPresentation(presentationKey, reason, error = null) {
			if (presentationKey !== this.adbutlerPreparedKey && presentationKey !== this.adbutlerStartRequestedKey) {
				return;
			}
			console.warn(`AdButler Takeover skipped: ${reason}`, error || '');
			this.adbutlerFailedKey = presentationKey;
			// A failed prefetch belongs to the upcoming slot and must not be requested again at its boundary.
			if (!this.adbutlerStartRequestedKey) {
				this.adbutlerFailedSlotIndex = this.adbutlerPreparedSlotIndex;
			}
			this.finishAdbutlerPresentation();
		},
		finishAdbutlerPresentation({ trackClose = false } = {}) {
			if (trackClose && this.adbutlerVisible) {
				this.adbutlerTracker?.close();
			}
			this.adbutlerVisible = false;
			this.clearAdbutlerMedia();
		},
		// Invalidate pending work before clearing the media so late VAST or media events cannot revive a skipped ad.
		clearAdbutlerMedia(invalidateRequest = true) {
			if (invalidateRequest) {
				this.adbutlerRequestId += 1;
			}
			clearTimeout(this.adbutlerReadinessGraceTimeout);
			clearTimeout(this.adbutlerSafetyTimeout);
			this.adbutlerReadinessGraceTimeout = null;
			this.adbutlerSafetyTimeout = null;
			this.adbutlerReady = false;
			this.adbutlerStartRequestedKey = null;
			this.adbutlerPreparedKey = null;
			this.adbutlerPreparedSlotIndex = null;
			this.adbutlerCreative = null;
			this.adbutlerTracker = null;

			const video = this.$refs.adVideo;
			if (video) {
				video.pause();
				video.removeAttribute('src');
				video.load();
			}
		},
		resetAdbutler(trackClose = false) {
			if (trackClose && this.adbutlerVisible) {
				this.adbutlerTracker?.close();
			}
			this.adbutlerVisible = false;
			this.adbutlerFailedKey = null;
			this.adbutlerFailedSlotIndex = null;
			this.adbutlerObservedSlotIndex = null;
			this.adbutlerWasInStartWindow = false;
			this.clearAdbutlerMedia();
		},
		appendAdAttributes(url) {
			if (!url) {
				return url;
			}

			let imageUrl = this.event ? `${url}/${this.event.id}` : url;

			if (this.event && storage.viewerId) {
				imageUrl = `${imageUrl}/${storage.viewerId}`;
			}

			return imageUrl;
		},
	},
};
</script>

<style lang="scss" scoped>
.monetization-takeover-renderer {
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	z-index: 0;
	pointer-events: none;
}

.monetization-takeover-renderer__video {
	display: block;
	width: 100%;
	height: 100%;
	object-fit: cover;
}
</style>
